import { PropType } from 'vue';
import { Options, Vue } from 'vue-class-component';
import {
  AddressingType,
  ApartmentLabelDTO,
  EntrancePlantDetailDTO,
} from '@/../client-generator/generated-clients/plants';
import { mapState } from 'vuex';
import { lineIsNullOrEmpty } from '@/core/utils/utils';

@Options({
  name: 'DialogEditLabel',
  props: {
    isEdit: {
      type: Boolean,
      required: true,
    },
    labelToEdit: {
      type: Object as PropType<ApartmentLabelDTO>,
      required: false,
    },
    isVisible: {
      type: Boolean,
      required: true,
    },
    addressingType: {
      type: Number,
      required: true,
    },
  },
  watch: {
    isVisible(newValue) {
      if (newValue) {
        this.loadLabel();
      }
    },
    isInformationVisible(newValue) {
      if (!newValue) {
        this.information = '';
      }
    },
  },
  computed: {
    ...mapState(['currentEntrancePlant']),
  },
  emits: ['hide-dialog', 'save-label'],
})
export default class DialogEditLabel extends Vue {
  labelToEdit: ApartmentLabelDTO | undefined;
  addressingType!: AddressingType;
  currentEntrancePlant!: EntrancePlantDetailDTO;

  // editing fields
  isPrivate = true;
  surname = '';
  name = '';
  companyName = '';
  information = '';
  imageName = '';
  imageBase64 = '';

  readonly informationMaxLength = 225;
  readonly maxLineFieldLength = 25;
  isInformationVisible: boolean = false;

  isVisible!: boolean;
  isEdit!: boolean;

  handleHideDialog() {
    this.$emit('hide-dialog');
  }

  get dialogTitle() {
    if (this.labelToEdit?.labelId !== undefined) {
      return this.$t('AppResourceEP.EditLabel');
    } else {
      return this.$t('AppResourceEP.AddLabel');
    }
  }

  get isBRFA() {
    return this.addressingType === AddressingType.BuildingRiserFloorApartment;
  }

  get isCallingCode() {
    return (
      this.addressingType === AddressingType.DirectCall_Alphanumeric ||
      this.addressingType === AddressingType.DirectCall_NumbersOnly
    );
  }

  get remainingChars() {
    if (!this.information) return this.informationMaxLength;
    return this.informationMaxLength - this.information.length;
  }

  get saveButtonDisabled() {
    let disabled = false;
    switch (this.addressingType) {
      case AddressingType.AddressBook:
        if (this.isPrivate) {
          if (!this.surname && !this.name) {
            disabled = true;
          }
        } else {
          if (!this.companyName) {
            disabled = true;
          }
        }
        break;

      case AddressingType.DirectCall_Alphanumeric:
      case AddressingType.DirectCall_NumbersOnly:
        if (lineIsNullOrEmpty(this.surname) && !lineIsNullOrEmpty(this.name)) {
          disabled = true;
        }
        break;
    }
    
    if (this.isInformationVisible && !this.information) disabled = true;

    return disabled;
  }

  loadLabel() {
    if (this.isEdit && this.labelToEdit) {
      this.isPrivate = this.labelToEdit.isPrivate ?? true;
      this.surname = this.labelToEdit.surname ?? '';
      this.name = this.labelToEdit.name ?? '';
      this.companyName = this.labelToEdit.companyName ?? '';
      this.information = this.labelToEdit.information ?? '';
      this.imageName = this.labelToEdit.imageName ?? '';
      this.isInformationVisible = this.labelToEdit.information ? true : false;
    } else {
      this.isPrivate = true;
      this.surname = '';
      this.name = '';
      this.companyName = '';
      this.information = '';
      this.imageName = '';
      this.imageBase64 = '';
      this.isInformationVisible = false;
    }
  }

  triggerFilePicker() {
    document!.getElementById('fileInput')!.click();
  }

  onFileSelected(event: { target: { files: any[] } }) {
    const file = event.target.files[0];
    if (file) {
      const img = new Image();
      img.src = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(img.src);
        if (
          file.size > 3 * 1024 * 1024 ||
          img.width !== 292 ||
          img.height !== 292
        ) {
          this.$toast.add({
            severity: 'error',
            summary: this.$t('AppResourceEP.NoImageAdded'),
            closable: false,
            life: 3000,
          });
        } else {
          const reader = new FileReader();
          reader.onload = (loadEvent) => {
            //split from the first part 'data:image/***;base64,'
            this.imageBase64 = (loadEvent.target!.result as string).split(',')[1];
            this.imageName = file.name;
          };
          reader.readAsDataURL(file);
        }
      };
    }
  }

  async handleButtonSubmitAction() {
    const savingLabel = new ApartmentLabelDTO({
      isPrivate: this.isPrivate,
      surname: this.isPrivate ? this.surname : '',
      name: this.isPrivate ? this.name : '',
      companyName: !this.isPrivate ? this.companyName : '',
      information: this.information,
      imageBase64: this.imageBase64,
      imageName: this.imageName,
    });
    if (this.labelToEdit) {
      savingLabel.labelId = this.labelToEdit.labelId;
    }
    this.$emit('save-label', savingLabel);
  }

  removeImage() {
    this.imageBase64 = '';
    this.imageName = '';
  }
}
